import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Eye, Printer, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, metricLabel, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const WEATHER_OPTIONS = ["CLEAR", "CLOUDY", "RAIN", "STORM"];

export default function DailyReports() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/daily-reports`);
      setReports(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (report) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/daily-reports/${report.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReport = async () => {
    setBusyId(detail.id);
    try {
      await api.post(`/projects/${projectId}/daily-reports/${detail.id}/submit`);
      toast.success("Report submitted for approval");
      await openDetail(detail);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const approveReport = async () => {
    setBusyId(detail.id);
    try {
      await api.post(`/projects/${projectId}/daily-reports/${detail.id}/approve`);
      toast.success("Report approved");
      await openDetail(detail);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <PageHeader
        title="Daily Progress Report"
        description="One report per day, assembled from that day's progress entries, resources and delays. Locks after submission."
        action={<CreateDailyReportDialog projectId={projectId} onCreated={load} />}
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && reports.length === 0 ? (
          <EmptyState title="No daily reports yet" testId="daily-reports-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Weather</TableHead><TableHead>Site incharge</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id} data-testid={`daily-report-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-foreground">{r.number}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.report_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.weather || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.site_incharge || "—"}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`view-daily-report-button-${r.id}`}><Eye className="h-3.5 w-3.5" /> View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="daily-report-detail-dialog">
          {detailLoading || !detail ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div id="daily-report-print-area">
              <DialogHeader>
                <DialogTitle>Daily Progress Report — {detail.number}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <Field label="Date" value={fmtDate(detail.report_date)} />
                <Field label="Weather" value={detail.weather || "—"} />
                <Field label="Site incharge" value={detail.site_incharge || "—"} />
                <Field label="Status" value={<StatusPill value={detail.status} />} />
              </div>
              {detail.remarks && <p className="mt-3 rounded-lg bg-muted/50 p-2.5 text-xs text-muted-foreground" data-testid="daily-report-remarks">{detail.remarks}</p>}

              <Section title="Progress entries" testId="daily-report-progress-entries">
                {detail.progress_entries.length === 0 ? <EmptyRow /> : (
                  <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Activity</TableHead><TableHead>Metric</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.progress_entries.map((p) => (
                      <TableRow key={p.wbs_item_id + p.metric}><TableCell className="font-mono text-xs">{p.code}</TableCell><TableCell className="text-xs">{p.name}</TableCell><TableCell className="text-xs text-muted-foreground">{metricLabel(p.metric)}</TableCell><TableCell className="text-right font-mono text-xs">{fmtNum(p.quantity, 2)}</TableCell><TableCell className="text-xs text-muted-foreground">{p.note || "—"}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </Section>

              <Section title="Resources on site" testId="daily-report-resources">
                {detail.resources.length === 0 ? <EmptyRow /> : (
                  <Table><TableHeader><TableRow><TableHead>Trade</TableHead><TableHead>Contractor</TableHead><TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Actual</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.resources.map((r, i) => (
                      <TableRow key={i}><TableCell className="text-xs">{r.trade}</TableCell><TableCell className="text-xs text-muted-foreground">{r.contractor || "—"}</TableCell><TableCell className="text-right font-mono text-xs">{fmtNum(r.planned_count, 2)}</TableCell><TableCell className="text-right font-mono text-xs font-semibold">{fmtNum(r.actual_count, 2)}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </Section>

              <Section title="Delays raised that day" testId="daily-report-delays">
                {detail.delays.length === 0 ? <EmptyRow /> : (
                  <ul className="space-y-1.5">{detail.delays.map((d) => (
                    <li key={d.id} className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{d.category}</span> · {d.reason} ({d.impact_days}d)</li>
                  ))}</ul>
                )}
              </Section>

              <Section title="Goods issues that day" testId="daily-report-goods-issues">
                {detail.goods_issues.length === 0 ? <EmptyRow /> : (
                  <ul className="space-y-1.5">{detail.goods_issues.map((g) => (
                    <li key={g.id} className="font-mono text-xs text-muted-foreground">{g.number}</li>
                  ))}</ul>
                )}
              </Section>

              <div className="mt-5 flex flex-wrap justify-end gap-2 print:hidden">
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-daily-report-button"><Printer className="h-3.5 w-3.5" /> Print</Button>
                {detail.status === "DRAFT" && (
                  <Button size="sm" disabled={busyId === detail.id} onClick={submitReport} data-testid="submit-daily-report-button">
                    {busyId === detail.id && <Loader2 className="h-4 w-4 animate-spin" />} <Send className="h-3.5 w-3.5" /> Submit
                  </Button>
                )}
                {detail.status === "SUBMITTED" && isProjectManager && (
                  <Button size="sm" disabled={busyId === detail.id} onClick={approveReport} data-testid="approve-daily-report-button">
                    {busyId === detail.id && <Loader2 className="h-4 w-4 animate-spin" />} <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-foreground">{value}</div>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <div className="mt-5" data-testid={testId}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

function EmptyRow() {
  return <p className="text-xs text-muted-foreground">None recorded.</p>;
}

function CreateDailyReportDialog({ projectId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ report_date: todayISO(), weather: "", site_incharge: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/daily-reports`, { ...form, weather: form.weather || null });
      toast.success("Daily report created");
      setForm({ report_date: todayISO(), weather: "", site_incharge: "", remarks: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-daily-report-button"><Plus className="h-4 w-4" /> New Report</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New Daily Report</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-daily-report-form">
          <div><Label>Date</Label><Input required type="date" value={form.report_date} onChange={set("report_date")} className="mt-1.5" data-testid="daily-report-date-input" /></div>
          <div>
            <Label>Weather (optional)</Label>
            <Select value={form.weather || "__none__"} onValueChange={(v) => setForm({ ...form, weather: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="daily-report-weather-select"><SelectValue placeholder="Not recorded" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not recorded</SelectItem>
                {WEATHER_OPTIONS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Site incharge</Label><Input value={form.site_incharge} onChange={set("site_incharge")} className="mt-1.5" data-testid="daily-report-site-incharge-input" /></div>
          <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} className="mt-1.5" data-testid="daily-report-remarks-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-daily-report-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
